import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';

import {
    useDisconnectProviderMutation,
    useProviderDisconnectPreviewQuery,
} from './useProviderConnectionsQuery';
import {ProviderConnection} from '../types';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1248 — DISCONNECT, GATED ON THE PREVIEW.
//
// ⚠️ THE PREVIEW IS NOT ADVISORY. `disconnectProvider` does not ask for
// confirmation because a server cannot verify that a human saw anything — the
// schema says so explicitly, and the preview exists so the interface can be the
// thing that checks. This hook is where that guarantee is implemented:
// `canConfirm` is false until a preview has actually resolved, so the confirm
// button cannot be armed by a modal that failed to load its preview.
//
// ⚠️ THERE IS NO BARE DELETE PATH IN THIS MODULE. No `deleteRecord`, no row
// action that calls the mutation directly, no batch delete over selected rows.
// Disconnecting revokes a tenant's integration and destroys a secret; the cost
// is not visible from the row, which is the entire reason the preview query
// exists on the schema.

export const useProviderDisconnect = () => {

    const [connection, setConnection] = useState<ProviderConnection | undefined>(undefined);
    const [opened, setOpened] = useState(false);

    // ⚠️ Gated on `opened`, and gcTime 0 in the query — the preview is re-read
    // every time the modal opens. A cached count from ten minutes ago is the
    // one number that must not be stale on this screen.
    const previewQuery = useProviderDisconnectPreviewQuery(connection?.id, opened);

    const disconnectMutation = useDisconnectProviderMutation();

    const requestDisconnect = (target: ProviderConnection) => {
        // Opening RESETS the previous failure, or a refusal from a prior
        // attempt on a different connection renders against this one.
        disconnectMutation.reset();
        setConnection(target);
        setOpened(true);
    };

    const cancelDisconnect = () => {
        setOpened(false);
        // ⚠️ The connection is NOT cleared here — Mantine's Modal animates out,
        // and clearing it now blanks the dialog mid-transition.
    };

    const confirmDisconnect = async () => {
        if (!connection) {
            return;
        }
        try {
            const result = await disconnectMutation.mutateAsync(connection.id);

            // ⚠️ REPORTS WHAT WAS OBSERVED, NOT WHAT WAS INTENDED. Every field
            // on the result is an observed fact: `secretDeleted` is true only
            // when Secrets Manager confirmed it. A connection whose record went
            // but whose secret did not is a real outcome an operator must be
            // told about, not smoothed into "Disconnected".
            if (result && result.connectionDeleted && !result.secretDeleted) {
                showSuccessNotification(I18n.get('Connection removed, but its stored secret was not confirmed deleted. Report this so it can be cleaned up.'));
            } else {
                showSuccessNotification(I18n.get('Provider disconnected. Its stored credential has been deleted.'));
            }

            // ONLY ON SUCCESS. A refused disconnect keeps the modal OPEN so the
            // tenant can read why.
            setOpened(false);
        } catch (e) {
            showErrorNotifications(e);
        }
    };

    return {
        connection,
        opened,
        requestDisconnect,
        cancelDisconnect,
        confirmDisconnect,
        preview: previewQuery.data ?? undefined,
        isPreviewLoading: previewQuery.isLoading,
        isPreviewError: previewQuery.isError,
        retryPreview: () => previewQuery.refetch(),
        isDisconnecting: disconnectMutation.isPending,
        error: disconnectMutation.error,
        // ⚠️ THE GATE. Not "the modal is open" and not "the preview finished
        // loading" — the preview must have RESOLVED WITH DATA. A failed preview
        // leaves this false, so a tenant can never confirm a disconnect whose
        // consequences were never displayed.
        canConfirm: Boolean(previewQuery.data) && !disconnectMutation.isPending,
    };
};
